import type { FlagsType } from '@iheartjane/feature-flags';
import { useEffect, useRef } from 'react';

import {
  registerMixpanelIdentity,
  registerSuperPropertiesStore,
} from '@jane/bloom/services/tracking/mixpanel';

type UseMixpanelRegistrationParams = {
  buildVersion: string;
  featureFlags?: FlagsType;
  operatorId: number;
  operatorName: string;
  pluginVersion?: string;
  projectToken?: string | null;
  storeCity?: string;
  storeId?: number;
  storeName?: string;
  storeState?: string;
  uniqueId: string;
};

/**
 * Registers user identity and super properties with Mixpanel.
 *
 * Identity (`mixpanel.identify` + the full super-property payload) is
 * registered once per uniqueId. The pinned store can still change without
 * an identity change — e.g. URL-driven store overrides (`/store/:id/...`),
 * custom slug routes, or cookie/loader-data updates that bypass the
 * StoreSwitcher — so we additionally re-register the store-related super
 * properties whenever `storeId` changes. Without this, events fire with the
 * previously-registered (often stale, sometimes from a prior session via
 * localStorage persistence) storeId super prop.
 */
export function useMixpanelRegistration({
  projectToken,
  storeCity,
  storeId,
  storeName,
  storeState,
  uniqueId,
  ...rest
}: UseMixpanelRegistrationParams) {
  const registeredUniqueIdRef = useRef<string | null>(null);
  const registeredStoreIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!projectToken || !storeCity || !storeId || !storeName || !storeState) {
      return;
    }

    if (uniqueId !== registeredUniqueIdRef.current) {
      registeredUniqueIdRef.current = uniqueId;
      registeredStoreIdRef.current = storeId;
      registerMixpanelIdentity({
        storeCity,
        storeId,
        storeName,
        storeState,
        uniqueId,
        ...rest,
      });
    } else if (storeId !== registeredStoreIdRef.current) {
      registeredStoreIdRef.current = storeId;
      registerSuperPropertiesStore({
        storeCity,
        storeId,
        storeName,
        storeState,
      });
    }
  }, [projectToken, rest, storeCity, storeId, storeName, storeState, uniqueId]);
}
